// SPDX-License-Identifier: GPL-3.0-or-later
//
// Export the user's root folder to a ZIP (download) and import a ZIP back
// into the root. Used for backup today; sets up the lite-mode (Firefox /
// Safari) path where data lives in IndexedDB and moves between browsers via
// ZIP.

import { unzip, zip, type Unzipped, type Zippable } from 'fflate';

const ROOT_NAME = 'applify-export';
const SKIP = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

async function walkInto(
  dir: FileSystemDirectoryHandle,
  prefix: string,
  out: Zippable,
): Promise<void> {
  for await (const [name, handle] of dir.entries()) {
    if (SKIP.has(name)) continue;
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'directory') {
      await walkInto(handle as FileSystemDirectoryHandle, path, out);
    } else {
      const file = await (handle as FileSystemFileHandle).getFile();
      out[path] = new Uint8Array(await file.arrayBuffer());
    }
  }
}

function zipAsync(input: Zippable): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(input, { level: 6 }, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

function unzipAsync(input: Uint8Array): Promise<Unzipped> {
  return new Promise((resolve, reject) => {
    unzip(input, (err, data) => {
      if (err) reject(err);
      else resolve(data);
    });
  });
}

export interface ExportResult {
  blob: Blob;
  filename: string;
  fileCount: number;
}

export async function exportRootAsZip(
  root: FileSystemDirectoryHandle,
): Promise<ExportResult> {
  const entries: Zippable = {};
  await walkInto(root, '', entries);
  const fileCount = Object.keys(entries).length;
  const data = await zipAsync(entries);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return {
    // Wrap to keep TS happy about ArrayBuffer-vs-SharedArrayBuffer narrowing.
    blob: new Blob([new Uint8Array(data)], { type: 'application/zip' }),
    filename: `${ROOT_NAME}-${stamp}.zip`,
    fileCount,
  };
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

async function ensureNestedDir(
  root: FileSystemDirectoryHandle,
  segments: string[],
): Promise<FileSystemDirectoryHandle> {
  let cur: FileSystemDirectoryHandle = root;
  for (const seg of segments) {
    if (!seg) continue;
    cur = await cur.getDirectoryHandle(seg, { create: true });
  }
  return cur;
}

export interface ImportResult {
  written: number;
  skipped: number;
}

export interface ImportOptions {
  // What to do when a destination file already exists.
  onConflict: 'overwrite' | 'skip';
}

export async function importZipIntoRoot(
  root: FileSystemDirectoryHandle,
  zipBlob: Blob,
  opts: ImportOptions = { onConflict: 'overwrite' },
): Promise<ImportResult> {
  const bytes = new Uint8Array(await zipBlob.arrayBuffer());
  const entries = await unzipAsync(bytes);

  let written = 0;
  let skipped = 0;

  for (const [path, data] of Object.entries(entries)) {
    if (!data || path.endsWith('/')) continue; // directory marker
    if (SKIP.has(path.split('/').pop() ?? '')) continue;
    const parts = path.split('/');
    const name = parts.pop();
    if (!name) continue;
    const dir = await ensureNestedDir(root, parts);
    if (opts.onConflict === 'skip') {
      try {
        await dir.getFileHandle(name);
        skipped++;
        continue;
      } catch { /* doesn't exist — proceed */ }
    }
    const fh = await dir.getFileHandle(name, { create: true });
    const w = await fh.createWritable();
    try { await w.write(new Blob([data])); }
    finally { await w.close(); }
    written++;
  }
  return { written, skipped };
}
